import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { workflowsApi, extractOne } from '../../api/facades';

const inputClass = 'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const WorkflowEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = id ? parseInt(id, 10) : NaN;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const { data: res, isLoading } = useQuery({
    queryKey: ['workflows', numId],
    queryFn: () => workflowsApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const workflow = extractOne(res);

  useEffect(() => {
    if (workflow && typeof workflow === 'object') {
      const w = workflow as any;
      setName(w.name ?? '');
      setDescription(w.description ?? '');
    }
  }, [workflow]);

  const update = useMutation({
    mutationFn: () => workflowsApi.update(numId, { name: name.trim(), description: description.trim() || null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      toast.success('Workflow updated.');
      navigate(`/dashboard/workflows/${numId}`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || e?.message || 'Update failed.'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    update.mutate();
  };

  if (Number.isNaN(numId)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6"><p className="text-slate-600">Invalid ID.</p><Button variant="secondary" className="mt-4" onClick={() => navigate(-1)}>Back</Button></div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">Edit Workflow</h1>
      {isLoading ? <p className="text-slate-500">Loading…</p> : (
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
          <FormField label="Name" hint="Required">
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required />
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Description">
              <textarea className={inputClass} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </FormField>
          </div>
          <div className="md:col-span-2 flex gap-2">
            <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={!name.trim() || update.isPending}>{update.isPending ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default WorkflowEditPage;
